import { IPoint2d } from '@/editor/math/IPoint2d';
import { ArcSeg2d } from '@/editor/math/arcSeg2d';
import { Point2dUtils } from '@/editor/math/Point2dUtils';
import { Curve2dUtils } from '@/editor/math/Curve2dUtils';
import { calculatePolygonArea } from '@/editor/model/polygon/utils';
import { IWallEdge } from '@/editor/types';
import { getWallsStringV2 } from '.';
import { isArc } from '../arc';

// 兜底认领的门槛：新旧壁芯轮廓的重合度(交/并)
const MIN_OVERLAP_RATIO = 0.7;
// 兜底认领的第二条通道：新区域落在旧轮廓里的比例(交/新面积)。
// 「存档少记了绕行边」这个形态有个确定性质：存档轮廓 ⊇ 真实轮廓(多包含了本该被挖掉的那块)，
// 所以按包含率判比 IoU 稳 —— IoU 会随被挖掉部分的占比掉下去：实测大房间里嵌一间占 31.8% 的房间时，
// 新区域(C 形 1708.15㎡) 与存档(纯外框 2505.68㎡) 的 IoU 只有 0.678，够不到 0.7 就被删掉重建了，
// 而包含率恒为 1.00，与被挖掉部分的大小无关。
const MIN_CONTAIN_RATIO = 0.95;
// 包含率的可信下限：新区域在采样网格里至少要占到这么多个格子。
// aInB 是 countBoth/countA，countA 只有个位数时(新区域相对包围盒极小)这个比值不可信 ——
// 极端情况 1 个采样点就能得出 1.00。样本不够就不走这条通道，交给 IoU 判。
const MIN_CONTAIN_SAMPLES = 16;
// 重合度采样网格的边长(格数)
const SAMPLE_COUNT = 64;
// 壁芯环里的弧边按固定段数打散：重合度只要判「占的是不是同一块地方」，
// 不需要 dispersePath 那种按弦高自适应(单条弧最多 500 段)的精度 —— 多弧房间会有上千个顶点，
// 采样时的点在多边形判定要乘上顶点数，代价直接上亿次。
const ARC_SEGMENT_COUNT = 16;

interface IRoomCorePath {
  corePathInfos: IWallEdge[];
}

export interface IRoomMatchResult {
  roomId?: string;
  // true: 壁芯边序列完全一致；false: 靠轮廓重合度兜底认领(边界与旧数据不一致)
  exact: boolean;
}

const getEdgeKeys = (edges: IWallEdge[]) => edges.map((edge) => `${edge.wallId}+${edge.side}`);

// 壁芯边(首尾相连的环) → 离散多边形，弧边打散成折线。
// 不按边数提前判空：整圆房间只有两条半圆边，打散后才够 3 个点(调用处按点数过滤)。
export function getCorePolygon(corePathInfos: IWallEdge[]): IPoint2d[] {
  const points: IPoint2d[] = [];
  corePathInfos.forEach((edge) => {
    if (!isArc(edge)) {
      points.push(edge.start);
      return;
    }

    const arc = new ArcSeg2d({ start: edge.start, end: edge.end, center: edge.center, clockwise: edge.clockwise });
    // 末点交给下一条边的起点，避免重复
    points.push(...arc.getPoints(ARC_SEGMENT_COUNT).slice(0, -1));
  });

  // 每条边只取了起点，环没闭合时(理论上不该出现)补上最后一个终点
  const last = corePathInfos[corePathInfos.length - 1]?.end;
  if (last && points.length && !Point2dUtils.equals(points[0], last)) points.push(last);

  return points;
}

// 两个壁芯多边形的重合关系，用均匀网格采样估算：
//   iou  = 交/并          —— 判「是不是同一块地方」
//   aInB = 交/a 的面积    —— 判「a 是不是整块落在 b 里」(兜底的第二条通道用)
// 不能用多边形布尔运算：壁芯环遇到「搭在房间边界上的墙」时会沿同一条中线出去再回来，
// 形成一条零宽缝(自接触多边形)。polybool 在这种环上会把缝两侧算重 —— 实测本例交集 237.63㎡
// 比两个输入(222.90㎡ / 230.27㎡)都大、算出 IoU 1.10，完全不可用；采样法对自接触环和凹多边形都稳。
export function getPolygonOverlapStats(
  a: IPoint2d[],
  b: IPoint2d[],
  sampleCount: number = SAMPLE_COUNT
): { iou: number; aInB: number; aSamples: number } {
  const NONE = { iou: 0, aInB: 0, aSamples: 0 };
  if (a.length < 3 || b.length < 3) return NONE;

  // 顶点可能上千(多弧房间)，包围盒用循环算，不要 Math.min(...points) 那种展开
  const getBox = (points: IPoint2d[]) => {
    const box = { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity };
    points.forEach(({ x, y }) => {
      box.minX = Math.min(box.minX, x);
      box.maxX = Math.max(box.maxX, x);
      box.minY = Math.min(box.minY, y);
      box.maxY = Math.max(box.maxY, y);
    });
    return box;
  };
  const boxA = getBox(a);
  const boxB = getBox(b);
  // 包围盒都不相交，谈不上重合
  if (boxA.minX > boxB.maxX || boxB.minX > boxA.maxX || boxA.minY > boxB.maxY || boxB.minY > boxA.maxY) return NONE;

  const minX = Math.min(boxA.minX, boxB.minX);
  const maxX = Math.max(boxA.maxX, boxB.maxX);
  const minY = Math.min(boxA.minY, boxB.minY);
  const maxY = Math.max(boxA.maxY, boxB.maxY);

  let countA = 0;
  let countB = 0;
  let countBoth = 0;
  for (let i = 0; i < sampleCount; i++) {
    const x = minX + ((i + 0.5) * (maxX - minX)) / sampleCount;
    for (let j = 0; j < sampleCount; j++) {
      const y = minY + ((j + 0.5) * (maxY - minY)) / sampleCount;
      const point = { x, y };
      const inA = Curve2dUtils.isPointInPolygon(point, a);
      const inB = Curve2dUtils.isPointInPolygon(point, b);
      if (inA) countA++;
      if (inB) countB++;
      if (inA && inB) countBoth++;
    }
  }

  const union = countA + countB - countBoth;
  return {
    iou: union > 0 ? countBoth / union : 0,
    aInB: countA > 0 ? countBoth / countA : 0,
    aSamples: countA
  };
}

export function getPolygonOverlapRatio(a: IPoint2d[], b: IPoint2d[], sampleCount: number = SAMPLE_COUNT): number {
  return getPolygonOverlapStats(a, b, sampleCount).iou;
}

/**
 * 为每个新区域找出它对应的旧房间(返回与 areas 等长的数组，认领不到时 roomId 为 undefined)。
 *
 * 两轮匹配：
 *  1. 壁芯边序列(wallId+side 的规范化序列)完全一致 —— 原有逻辑；
 *  2. 兜底：旧版本存下来的 corePathInfos 不含「墙搭在房间边界上、轮廓要沿它出去再绕回来」这类边，
 *     序列 key 天然对不上，但描述的仍是同一间房；没有兜底这些房间会被删掉重建，
 *     材质/名称/分区/隔墙关联全部丢失。只在「旧边集 ⊆ 新边集」且轮廓关系成立时认领，
 *     轮廓关系有两条通道：IoU ≥ 0.7（挖掉的那块很小）或 新区域整块落在存档轮廓里 ≥ 0.95
 *     （挖掉多大都成立 —— 存档轮廓天然是真实轮廓的超集）。
 *
 * 第一轮的行为与原逻辑逐字等价；第二轮只在第一轮认领不到的位置上做，且不会碰第一轮认领走的房间。
 */
export function matchRoomsByCorePath(
  areas: IRoomCorePath[],
  rooms: (IRoomCorePath & { id: string })[]
): IRoomMatchResult[] {
  const matchResults: IRoomMatchResult[] = areas.map(() => ({ exact: false }));
  const usedRoomIds = new Set<string>();

  // ===== 第一轮：壁芯边序列精确匹配 =====
  // 与原逻辑逐字等价：key 相同就认，多个区域算出同一个 key 时都指向同一个旧房间
  // (原来就是这样：后一个 updateRoomExtraData 覆盖前一个，且不会多建房间) —— 不要在这里加去重，
  // 那会改变既有行为。
  const roomKeyMap = new Map<string, string>();
  rooms.forEach((room) => roomKeyMap.set(getWallsStringV2(getEdgeKeys(room.corePathInfos)), room.id));
  areas.forEach((area, index) => {
    const roomId = roomKeyMap.get(getWallsStringV2(getEdgeKeys(area.corePathInfos)));
    if (!roomId) return;

    usedRoomIds.add(roomId);
    matchResults[index] = { roomId, exact: true };
  });

  // ===== 第二轮：按壁芯轮廓重合度兜底认领 =====
  // 必须等第一轮全部结束再做，否则前面某个区域可能按几何抢走后面能精确匹配上的房间。
  const pendingAreas = areas.map((area, index) => ({ area, index })).filter(({ index }) => !matchResults[index].roomId);
  const freeRooms = rooms.filter((room) => !usedRoomIds.has(room.id));
  if (!pendingAreas.length || !freeRooms.length) return matchResults;

  const candidates = freeRooms
    .map((room) => {
      const polygon = getCorePolygon(room.corePathInfos);
      return {
        id: room.id,
        polygon,
        size: Math.abs(calculatePolygonArea(polygon)),
        edgeKeys: new Set(getEdgeKeys(room.corePathInfos))
      };
    })
    .filter((candidate) => candidate.polygon.length >= 3 && candidate.size > 0);

  const pairs: { index: number; roomId: string; iou: number; contain: number }[] = [];
  pendingAreas.forEach(({ area, index }) => {
    const polygon = getCorePolygon(area.corePathInfos);
    if (polygon.length < 3) return;

    const size = Math.abs(calculatePolygonArea(polygon));
    if (!size) return;

    const edgeKeys = new Set(getEdgeKeys(area.corePathInfos));
    candidates.forEach((candidate) => {
      // ① 旧房间的每一条壁芯边都必须仍在新区域里 —— 兜底只处理「存档少记了几条边、说的是同一圈边界」，
      //    也就是现在算出来会沿搭上来的墙绕一圈、而存档没有那几条边的情形。
      //    这一条最严，把「房间被拆分/合并、墙被删改」全挡在外面(那些情形保持原来的删掉重建)；
      //    嵌套在大房间里、不共用任何墙的独立房间也过不了。
      let isSubset = true;
      candidate.edgeKeys.forEach((key) => {
        if (!edgeKeys.has(key)) isSubset = false;
      });
      if (!isSubset) return;

      // ② 便宜的预筛：两条通道都不可能达标时直接跳过。
      //    IoU ≤ 小面积/大面积；包含率 ≤ 旧面积/新面积(交不可能超过旧房间自己的面积)
      const iouPossible = Math.min(size, candidate.size) / Math.max(size, candidate.size) >= MIN_OVERLAP_RATIO;
      const containPossible = size <= candidate.size / MIN_CONTAIN_RATIO;
      if (!iouPossible && !containPossible) return;

      // ③ 采样一次，两条通道任一达标即可认领：
      //    iou —— 新旧轮廓基本是同一块地方(被漏记的绕行边只挖掉一小块)
      //    contain —— 新区域整块落在存档轮廓里(存档把该挖掉的那块也算进去了，挖多大都成立)
      const { iou, aInB: contain, aSamples } = getPolygonOverlapStats(polygon, candidate.polygon);
      if (iou >= MIN_OVERLAP_RATIO || (contain >= MIN_CONTAIN_RATIO && aSamples >= MIN_CONTAIN_SAMPLES)) {
        pairs.push({ index, roomId: candidate.id, iou, contain });
      }
    });
  });

  // 重合度高的先配对，一个区域、一个旧房间都只认领一次。
  // IoU 作主键：真正几何吻合的配对优先，只靠包含率过关的(IoU 偏低)不会抢走它们。
  pairs.sort((a, b) => b.iou - a.iou || b.contain - a.contain);
  pairs.forEach(({ index, roomId }) => {
    if (matchResults[index].roomId || usedRoomIds.has(roomId)) return;

    usedRoomIds.add(roomId);
    matchResults[index] = { roomId, exact: false };
  });

  return matchResults;
}
